import type {
  OrgTicketCode,
  TicketAssigneeList,
  TicketAttachmentDownload,
  TicketLabelAdmin,
  TicketLabelList,
  TicketLinkCreateRequest,
  TicketMessageResult,
  TicketResponseTargets,
  TicketSettings,
  TicketStaffQueue,
  TicketStaffView,
  TicketAttachmentMimeType,
} from '@traceio-org/platform-contracts';
import type {
  StaffTicketCreateInput,
  StaffTicketReplyInput,
  TicketLabelCreateInput,
  TicketLabelUpdateInput,
  TicketStaffQueueQuery,
  TicketStaffUpdateInput,
} from '@traceio-org/platform-client';
import type { AdminTicketMethods } from './deps';

/** Rows per page of Trace's ticket queue (platform-api allows 1 to 100). */
export const TICKET_QUEUE_PAGE_SIZE = 50;

/**
 * The ticket reads and writes of Trace Admin (ADMIN_TICKETS_API). Every call carries an admin-audience token; reads
 * take the signal of the page that asked, writes are never aborted once sent. Text is trimmed here, once.
 */
export interface AdminTicketApi {
  /** Trace's queue: a view (inbox, mine, all) and filters, with the caller's Inbox and Mine counts. */
  listTicketQueue(query: TicketStaffQueueQuery, signal?: AbortSignal): Promise<TicketStaffQueue>;
  /** One ticket as Trace sees it; opening it marks it read for the caller. */
  getTicket(number: string, signal?: AbortSignal): Promise<TicketStaffView>;
  /** Status, priority, assignee, labels (the whole set) or the caller's subscription. */
  updateTicket(number: string, change: TicketStaffUpdateInput): Promise<TicketStaffView>;
  /** A reply to the customer or a Trace note, optionally changing the status along with it. */
  replyToTicket(number: string, request: StaffTicketReplyInput): Promise<TicketMessageResult>;
  /** Hand the ticket back to the customer's help desk with a note they read. */
  handBackTicket(number: string, note: string): Promise<TicketStaffView>;
  addTicketLink(number: string, request: TicketLinkCreateRequest): Promise<TicketStaffView>;
  removeTicketLink(number: string, linkId: string): Promise<TicketStaffView>;
  /** File a ticket for a customer (it starts in Todo, already with Trace). */
  createTicket(request: StaffTicketCreateInput): Promise<TicketStaffView>;
  /** Who a ticket can be assigned to (staff.tickets.manage): active staff whose role works tickets. */
  listTicketAssignees(signal?: AbortSignal): Promise<TicketAssigneeList>;
  listTicketLabels(signal?: AbortSignal): Promise<TicketLabelList>;
  createTicketLabel(request: TicketLabelCreateInput): Promise<TicketLabelAdmin>;
  updateTicketLabel(labelId: string, change: TicketLabelUpdateInput): Promise<TicketLabelAdmin>;
  readTicketSettings(signal?: AbortSignal): Promise<TicketSettings>;
  updateTicketSettings(responseTargets: TicketResponseTargets): Promise<TicketSettings>;
  /** An organization's ticket code (2 to 5 capital letters), or null for Trace's global T- numbers. */
  setOrgTicketCode(organizationId: string, code: string | null): Promise<OrgTicketCode>;
  /**
   * Upload one file for a message of an organization's ticket: presign, PUT the bytes to the signed URL, finalize.
   * Answers the attachment id that goes in attachmentIds.
   */
  uploadTicketAttachment(organizationId: string, file: File): Promise<string>;
  /** A short-lived download URL of an attachment. */
  ticketAttachmentDownload(attachmentId: string): Promise<TicketAttachmentDownload>;
}

/** The PUT of an upload's bytes failed at the storage (not platform-api): the attachment shows "Upload failed". */
export class TicketUploadError extends Error {
  constructor(readonly status: number) {
    super(`The attachment upload answered ${status}`);
    this.name = 'TicketUploadError';
  }
}

export interface AdminTicketApiDependencies {
  client: AdminTicketMethods;
  /** The call options of one request: the admin-audience token and the page's signal. */
  options: (signal?: AbortSignal) => Promise<{ accessToken: string; signal?: AbortSignal }>;
  /**
   * The PUT to the storage's signed URL. The browser's fetch by default, looked up at call time (tests stub it). It
   * carries only the headers platform-api signed: never the admin token, which must not leave for the storage host.
   */
  upload?: (url: string, init: RequestInit) => Promise<Response>;
}

export function createAdminTicketApi({
  client,
  options,
  upload,
}: AdminTicketApiDependencies): AdminTicketApi {
  const put = upload ?? ((url: string, init: RequestInit) => globalThis.fetch(url, init));
  return {
    async listTicketQueue(query, signal) {
      return client.adminTicketQueue({ limit: TICKET_QUEUE_PAGE_SIZE, ...query }, await options(signal));
    },
    async getTicket(number, signal) {
      return client.adminTicket(number, await options(signal));
    },
    async updateTicket(number, change) {
      return client.adminUpdateTicket(number, change, await options());
    },
    async replyToTicket(number, request) {
      return client.adminTicketReply(number, { ...request, body: request.body.trim() }, await options());
    },
    async handBackTicket(number, note) {
      return client.adminTicketHandBack(number, { note: note.trim() }, await options());
    },
    async addTicketLink(number, request) {
      return client.adminAddTicketLink(number, request, await options());
    },
    async removeTicketLink(number, linkId) {
      return client.adminRemoveTicketLink(number, linkId, await options());
    },
    async createTicket(request) {
      return client.adminCreateTicket(
        { ...request, title: request.title.trim(), body: request.body.trim() },
        await options(),
      );
    },
    async listTicketAssignees(signal) {
      return client.adminTicketAssignees(await options(signal));
    },
    async listTicketLabels(signal) {
      return client.adminTicketLabels(await options(signal));
    },
    async createTicketLabel(request) {
      return client.adminCreateTicketLabel(
        { ...request, name: request.name.trim(), group: request.group?.trim() || null },
        await options(),
      );
    },
    async updateTicketLabel(labelId, change) {
      return client.adminUpdateTicketLabel(labelId, change, await options());
    },
    async readTicketSettings(signal) {
      return client.adminTicketSettings(await options(signal));
    },
    async updateTicketSettings(responseTargets) {
      return client.adminUpdateTicketSettings({ responseTargets }, await options());
    },
    async setOrgTicketCode(organizationId, code) {
      return client.adminSetOrgTicketCode(organizationId, { code }, await options());
    },
    async uploadTicketAttachment(organizationId, file) {
      const presigned = await client.adminPresignTicketAttachment(
        {
          organizationId,
          fileName: file.name,
          // The page only offers TICKET_ATTACHMENT_MIME_TYPES; platform-api checks the bytes again at finalize.
          mime: file.type as TicketAttachmentMimeType,
          sizeBytes: file.size,
          kind: 'file',
        },
        await options(),
      );
      const answer = await put(presigned.upload.url, {
        method: presigned.upload.method,
        headers: presigned.upload.headers,
        body: file,
      });
      if (!answer.ok) throw new TicketUploadError(answer.status);
      const finalized = await client.adminFinalizeTicketAttachment(presigned.id, await options());
      return finalized.id;
    },
    async ticketAttachmentDownload(attachmentId) {
      return client.adminTicketAttachmentDownload(attachmentId, await options());
    },
  };
}
